import {Button, Card, CardContent, CardHeader, colors, Divider, Grid, makeStyles, Switch, Tab, Tabs, Theme, Typography} from "@material-ui/core";
import {useSnackbar} from "notistack";
import React, {useEffect, useState} from "react";
import useRouter from "use-react-router";
import validate from "validate.js";
import {Can} from "../../../components/Can";
import ConfirmDelete from "../../../components/ConfirmDelete";
import {LoadingOverlay} from "../../../components/LoadingOverlay/LoadingOverlay";
import anyLoading from "../../../utils/anyLoading";
import {getHandleFieldChange, getHasError} from "../../../utils/formHelpers";
import createBillingParty from "./createBillingParty.api";
import deleteBillingParty from "./deleteBillingParty.api";
import updateBillingParty, {UpdateBillingPartyInput} from "./updateBillingParty.api";
import BillingPartyMemberEdit from "../BillingPartyMemberDetails/BillingPartyMemberEdit";
import {BillingParty, BillingPartyMember} from "../../../graphql/billing_party.datatypes";

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        marginTop: theme.spacing(3),
        width: "100%",
        marginBottom: theme.spacing(2)
    },
    card: {},
    saveButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: colors.green[600],
        "&:hover": {
            backgroundColor: colors.green[900]
        }
    },
    deleteButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: theme.palette.error.light,
        "&:hover": {
            backgroundColor: theme.palette.error.main
        }
    },
    cloneButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: colors.green[400],
        "&:hover": {
            backgroundColor: colors.green[700]
        }
    },
    errorIcon: {
        marginRight: theme.spacing(1),
        color: theme.palette.error.main
    },
    marginBottom: {
        paddingLeft: theme.spacing(1),
        marginBottom: theme.spacing(3)
    },
    tabs: {
        marginTop: theme.spacing(3)
    },
    textField: {
        width: "100%"
    }
}));

const getBlankBillingPartyMember = (billingParty: BillingParty) => {
    return {
        __typename: "BillingPartyMember",
        billing_party_id: billingParty.id,
        id: -1,
        first_name: "",
        last_name: "",
        email: "",
        address_line_1: "",
        address_line_2: "",
        city: "",
        state: "",
        zipcode: "",
        country: "",
        primary_phone: "",
        primary_phone_code: "",
        secondary_phone: "",
        secondary_phone_code: "",
        notes: "",
        primary_paying_member: false,
        is_group_leader: false,

        billingParty
    };
};

export const BillingPartyEdit = (props: {
    billingParty: BillingParty;
    isAdmin: boolean;
    canEdit: boolean;
}) => {
    const classes = useStyles();
    const {billingParty, isAdmin, canEdit} = props;
    const isNewParty = billingParty.id === -1;
    const {history} = useRouter();
    const [open, setOpen] = React.useState(false);
    const {enqueueSnackbar} = useSnackbar();
    const [sendCreateBillingParty, createData] = createBillingParty();
    const [sendUpdateBillingParty, updateData] = updateBillingParty();
    const [sendDeleteBillingParty, deleteData] = deleteBillingParty();
    const [
        selectedPayingMember,
        setSelectedPayingMember
    ] = useState<BillingPartyMember | null>(null);

    const allMembersCancelled =
        billingParty &&
        billingParty.members &&
        billingParty.members.every(member => member.cancelled);

    //1. Set form and error state
    const [formState, setFormState] = useState({
        ...billingParty
    });
    const loadingData = anyLoading(createData, updateData, deleteData);
    const [errors, setErrors] = useState({
        isValid: false,
        errors: {}
    });

    useEffect(() => {
        setFormState({
            ...billingParty
        });
    }, [billingParty]);

    //2. Setup helper functions for form
    const handleFieldChange = getHandleFieldChange(setFormState);
    const hasError = getHasError(errors);

    //3. setup validation and error hook
    const validateSchema: any = {};

    useEffect(() => {
        const newErrors = validate(formState, validateSchema);
        const isValid = !newErrors;

        if (!selectedPayingMember) {
            if (
                billingParty.payingMembers &&
                billingParty.payingMembers.length > 0
            ) {
                setSelectedPayingMember(billingParty.payingMembers[0]);
            }
        } else {
            const checkMember =
                billingParty.payingMembers &&
                billingParty.payingMembers.find(
                    member => member.id === selectedPayingMember.id
                );
            if (checkMember && checkMember !== selectedPayingMember) {
                setSelectedPayingMember(checkMember);
            }
        }

        setErrors(state => ({
            ...state,
            isValid,
            errors: newErrors || {}
        }));
    }, [formState]);

    //4. Handle save form
    const onSaveClick = () => {
        const sanitisedData: UpdateBillingPartyInput = {
            data: {
                ...{
                    ...formState,
                    __typename: undefined,
                    booking: undefined,
                    members: undefined,
                    cost: undefined,
                    payingMembers: undefined,
                    outstandingBalance: undefined,
                    overduePayments: undefined,
                    totalRefunds: undefined,
                    payment_schedule: undefined,
                    payments: undefined,
                    group: undefined,
                    adjustments: undefined,
                    code: undefined,
                    totalPayments: undefined,
                    totalReceived: undefined,
                    totalCredit: undefined,
                    totalCreditCardFees: undefined,
                }
            }
        };

        sendUpdateBillingParty({variables: sanitisedData})
            .then(() => {
                enqueueSnackbar("Booking Party Updated.", {
                    variant: "success"
                });
            })
            .catch(() => {
                enqueueSnackbar("Booking Party Update Failed!", {
                    variant: "error"
                });
            });
    };

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleDelete = () => {
        sendDeleteBillingParty({variables: {id: formState.id}})
            .then(() => {
                enqueueSnackbar("Booking Party Deleted.", {
                    variant: "success"
                });
                history.push("/groups");
            })
            .catch(() => {
                enqueueSnackbar("Deleted Booking Party Failed!", {
                    variant: "error"
                });
            });

        handleClose();
    };

    return (
        <LoadingOverlay loading={loadingData.loading}>
            {isAdmin && (
                <Card className={classes.container}>
                    <CardHeader title="Edit Billing Party"/>
                    <Divider/>
                    <CardContent className={classes.card}>
                        <form>
                            <Grid container spacing={3}>
                                <Grid item md={3} xs={6}>
                                    <Grid
                                        component="label"
                                        container
                                        alignItems="center"
                                        spacing={1}
                                    >
                                        <Grid item>
                                            <Switch
                                                checked={
                                                    formState.credit_card_fee_paid
                                                }
                                                color="secondary"
                                                edge="start"
                                                name="credit_card_fee_paid"
                                                value={
                                                    formState.credit_card_fee_paid
                                                }
                                                disabled={true}
                                            />
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="h5">
                                                Credit card fee paid?
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item md={3} xs={6}>
                                    <Grid
                                        component="label"
                                        container
                                        alignItems="center"
                                        spacing={1}
                                    >
                                        <Grid item>
                                            <Switch
                                                checked={
                                                    formState.soft_cancelled
                                                }
                                                color="secondary"
                                                edge="start"
                                                name="soft_cancelled"
                                                onChange={handleFieldChange}
                                                value={formState.soft_cancelled}
                                                disabled={allMembersCancelled}
                                            />
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="h5">
                                                Soft Cancelled?
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item md={3} xs={6}>
                                    <Grid
                                        component="label"
                                        container
                                        alignItems="center"
                                        spacing={1}
                                    >
                                        <Grid item>
                                            <Switch
                                                checked={formState.disabled}
                                                color="secondary"
                                                edge="start"
                                                name="disabled"
                                                onChange={handleFieldChange}
                                                value={formState.disabled}
                                            />
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="h5">
                                                Disabled?
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item md={3} xs={6}>
                                    <Grid
                                        component="label"
                                        container
                                        alignItems="center"
                                        spacing={1}
                                    >
                                        <Grid item>
                                            <Switch
                                                checked={
                                                    formState.comp_credit_eligible
                                                }
                                                color="secondary"
                                                edge="start"
                                                name="comp_credit_eligible"
                                                onChange={handleFieldChange}
                                                value={
                                                    formState.comp_credit_eligible
                                                }
                                            />
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="h5">
                                                Comp Credit Eligible?
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12}>
                                    <Button
                                        className={classes.saveButton}
                                        onClick={onSaveClick}
                                        variant="contained"
                                        disabled={!errors.isValid}
                                    >
                                        Save
                                    </Button>
                                    <Can permission="groups.delete">
                                        <Button
                                            onClick={handleClickOpen}
                                            className={classes.deleteButton}
                                            variant="contained"
                                            disabled={
                                                isNewParty ||
                                                billingParty.members_count >
                                                0 ||
                                                billingParty.payments_count > 0
                                            }
                                        >
                                            Delete
                                        </Button>
                                    </Can>
                                </Grid>
                            </Grid>
                        </form>
                    </CardContent>
                </Card>
            )}

            <Card className={classes.container}>
                <CardHeader title="Paying Members"/>
                <Divider/>
                <CardContent className={classes.card}>
                    <Tabs
                        onChange={(event, value) =>
                            setSelectedPayingMember(value)
                        }
                        scrollButtons="auto"
                        value={selectedPayingMember}
                        variant="scrollable"
                    >
                        {billingParty.payingMembers &&
                            billingParty.payingMembers.map(payingMember => (
                                <Tab
                                    label={
                                        payingMember.email +
                                        (payingMember.primary_paying_member
                                            ? "*"
                                            : "")
                                    }
                                    key={payingMember.id}
                                    value={payingMember}
                                />
                            ))}
                        {canEdit && (
                            <Tab
                                label="Add"
                                value={getBlankBillingPartyMember(billingParty)}
                            />
                        )}
                    </Tabs>

                    <Grid container>
                        {selectedPayingMember && (
                            <Grid item xs={12}>
                                <BillingPartyMemberEdit
                                    canEdit={canEdit}
                                    isAdmin={isAdmin}
                                    billingPartyMember={selectedPayingMember}
                                    setBillingPartyMember={
                                        setSelectedPayingMember
                                    }
                                />
                            </Grid>
                        )}
                    </Grid>
                </CardContent>
            </Card>

            <ConfirmDelete
                onClose={handleClose}
                onDeleteClick={handleDelete}
                open={open}
            />
        </LoadingOverlay>
    );
};

export default BillingPartyEdit;
